import { useState, type ReactNode } from 'react';
import { SegmentedControl } from '@/shared/components';
import type { ArrivalPlaceholderResponse, ArrivalStepKind } from '@/shared/api';
import type { ArrivalFieldKey, ArrivalStepDraft } from './arrivalDraft';
import { arrivalInsertOptions, arrivalSegments } from './arrivalFields';
import { ArrivalTextField } from './ArrivalTextField';
import { GUIDE_COPY } from './guideCopy';

type Lang = 'De' | 'En';
const LANGUAGE: Record<Lang, string> = { De: 'Deutsch', En: 'Englisch' };

interface Props {
  guide: ArrivalStepKind;
  draft: ArrivalStepDraft;
  placeholders: ArrivalPlaceholderResponse[];
  onChange: (draft: ArrivalStepDraft) => void;
  /** Die Vorschau dieses einen Schritts, wie der Server ihn rendert. */
  preview: ReactNode;
}

/** Inhalt des Sheets: eine Sprache auf einmal, damit Titel und Text auch schmal lesbar bleiben. */
export function ArrivalStepForm({ guide, draft, placeholders, onChange, preview }: Props) {
  const [lang, setLang] = useState<Lang>('De');
  const example = GUIDE_COPY[guide].example;
  const options = arrivalInsertOptions(placeholders, guide);
  const english = lang === 'En';
  const field = (key: ArrivalFieldKey, label: string, singleLine: boolean): ReactNode => (
    <ArrivalTextField
      key={key}
      label={label}
      language={LANGUAGE[lang]}
      value={draft[key]}
      placeholder={example[key]}
      singleLine={singleLine}
      maxLength={singleLine ? 120 : 4000}
      required={!english}
      hint={english ? `Leer = deutscher ${singleLine ? 'Titel' : 'Text'}.` : undefined}
      options={options}
      segments={arrivalSegments(draft[key], placeholders, guide)}
      onChange={(value) => onChange({ ...draft, [key]: value })}
    />
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SegmentedControl
          size="touch"
          aria-label="Sprache"
          shape="square"
          value={lang}
          onChange={setLang}
          options={[
            { value: 'De', label: 'Deutsch' },
            { value: 'En', label: 'Englisch' },
          ]}
        />
        {!draft.titleEn.trim() && !draft.bodyEn.trim() && (
          <span className="text-ink-soft text-xs">Englisch leer – Gäste sehen Deutsch.</span>
        )}
      </div>
      {english ? field('titleEn', 'Titel', true) : field('titleDe', 'Titel', true)}
      {english ? field('bodyEn', 'Text', false) : field('bodyDe', 'Text', false)}
      {(!draft.titleDe.trim() || !draft.bodyDe.trim()) && (
        <p className="text-ink-soft text-xs">Titel und Text auf Deutsch sind Pflicht.</p>
      )}
      {/* Kein Zeitschloss für Parkhinweise: der Gast braucht sie, bevor das Fenster aufgeht. */}
      {guide === 'Arrival' && (
        <label className="text-ink-soft flex min-h-11 items-start gap-3 text-sm">
          <input
            type="checkbox"
            className="mt-1 size-4"
            checked={draft.timeLocked}
            onChange={(event) => onChange({ ...draft, timeLocked: event.target.checked })}
          />
          <span>
            Erst 12 Stunden vor Anreise zeigen
            <span className="text-ink-soft block text-xs">
              Der Gast sieht bis dahin nur den Titel mit Hinweis. Ein Schritt mit Türcode ist
              ohnehin gesperrt — dort bleibt der Text lesbar und nur der Code verdeckt.
            </span>
          </span>
        </label>
      )}
      {preview}
    </div>
  );
}
